import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import moment from 'moment'
import Modal from 'react-modal'

import { deserialize } from '../../../../utility/serialization'
import { programPresentationData, programPresentationTabs } from '../../../../reducers/programBuilderReducer'
import { setProgram } from '../../../../reducers/programPresentationReducer'
import ProgramPresentationPage from '../../ProgramLanding/ProgramPresentationPage/ProgramPresentationPage'
import Loader from '../../../common/loader/legacy-loaders/Op36Loader-web'

import styles from './PreviewModal.module.scss'

const PreviewModal = ({ showModal, closeModal, onSaveProgram, setShowSaveModal, loader }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const community = useSelector(state => state.user.current_user.communities[0])
  const programData = useSelector(programPresentationData)
  const programTabs = useSelector(programPresentationTabs)
  const previewMode = useSelector(state => state.programPresentation.course.previewMode)
  const {
    id,
    isDraft,
    enableRegistrationDates,
    registrationStartsAt,
    registrationEndsAt,
    enableProgramCapacity,
    spotsAvailable,
    enrollments_count,
    waitlistCount,
  } = useSelector(state => state.programBuilder)

  const computeRegistrationClosedReason = useMemo(() => {
    if (enableRegistrationDates && registrationStartsAt && registrationEndsAt &&
      !moment().tz(community.timezone).isBetween(registrationStartsAt, registrationEndsAt)
    ) {
      return 'registration_closed'
    } else if (enableProgramCapacity && spotsAvailable && (spotsAvailable - enrollments_count <= 0)) {
      return 'limit_reached'
    }
  }, [
    enableRegistrationDates,
    registrationStartsAt,
    registrationEndsAt,
    enableProgramCapacity,
    spotsAvailable,
    enrollments_count,
    community,
  ])

  const computeCanStudentsJoinWaitlist = useMemo(() => {
    const areRegistrationDatesPresent = enableRegistrationDates && registrationStartsAt && registrationEndsAt
    const registrationPeriodEnded = areRegistrationDatesPresent && moment().tz(community.timezone).isAfter(moment(registrationEndsAt))
    const isRegistrationClosed = areRegistrationDatesPresent && !moment().tz(community.timezone).isBetween(registrationStartsAt, registrationEndsAt)
    const isCapacityFull = enableProgramCapacity && spotsAvailable && (spotsAvailable - enrollments_count - waitlistCount <= 0)

    return (isCapacityFull && (!isRegistrationClosed || !areRegistrationDatesPresent)) || registrationPeriodEnded
  }, [
    enableRegistrationDates,
    registrationStartsAt,
    registrationEndsAt,
    enableProgramCapacity,
    spotsAvailable,
    enrollments_count,
    waitlistCount,
    community,
  ])

  useEffect(() => {
    dispatch(setProgram({
      program: {
        ...programData,
        community: deserialize(community),
        registrationClosedReason: computeRegistrationClosedReason,
        canStudentsJoinWaitlist: computeCanStudentsJoinWaitlist,
      },
      detailsTabs: programTabs,
    }))
  }, [programData, community, computeRegistrationClosedReason, computeCanStudentsJoinWaitlist, programTabs, dispatch])

  return (
    <Modal
      isOpen={showModal}
      onRequestClose={closeModal}
      contentLabel={t('programs.builder.previewModal.contentLabel')}
      className={styles['preview-program-modal-content']}
      autoFocus
    >
      <section className='d-flex flex-column h-100'>
        {!previewMode
          ? <Loader message={t('programs.builder.previewModal.loadingPreview')} />
          : <>
            <div className={styles['preview-header']}>
              <span className={`d-flex justify-content-center align-items-center w-100 h-100 ${styles['header-title']}`}>
                {t('programs.builder.previewModal.thisIsAPreview')}
              </span>
            </div>

            <ProgramPresentationPage />

            <div className={`row ${styles['preview-footer']}`}>
              <div className={`${styles['action-button']} col-12 col-sm-6 d-flex justify-content-center align-items-center`}>
                <button className={`btn btn-primary ml-sm-auto ${styles['back-button']}`} onClick={closeModal}>
                  {t('programs.builder.previewModal.backToEditing')}
                </button>
              </div>

              <div className={`${styles['action-button']} col-12 col-sm-6 d-flex justify-content-center align-items-center`}>
                <button
                  className={`btn btn-primary mr-sm-auto ${styles['save-button']}`}
                  onClick={(id && !isDraft) ? () => onSaveProgram(false) : () => { setShowSaveModal(true); closeModal() }}
                  disabled={loader}
                >
                  {(id && !isDraft) ? t('common.save') : t('programs.builder.previewModal.saveOrPublish')}
                </button>
              </div>
            </div>
          </>
        }
      </section>
    </Modal>
  )
}

PreviewModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  onSaveProgram: PropTypes.func.isRequired,
  setShowSaveModal: PropTypes.func.isRequired,
  loader: PropTypes.bool.isRequired,
}

export default PreviewModal
